// What you did this stage, counted as it happens.
//
// The round card between stages used to be a sentence of prose about the
// ground you had won. What a player actually wants there is the *scorecard* —
// how long the front took, what it cost the other army, what came out of the
// floor — and none of those four numbers can be recovered afterwards.
//
// Not derivable, which is why it is stored. Everything else the UI shows is
// read off the board or off the log precisely because derived state cannot go
// stale; a tally is the exception, and deliberately so:
//
//  - **the board cannot say it.** A stage ends with the beaten army swept into
//    scrap, so "pieces taken" is gone from the position the moment it is true.
//  - **the log can only say it by replay**, and a run saves as a *snapshot*
//    rather than as a log — so there is no log to replay on a run continued in
//    a fresh tab.
//
// It is **not in the hash**. `hash.ts` enumerates the fields it covers rather
// than walking the state, so this is invisible to it by construction — which
// is right: two clients disagreeing about a scorecard is not a desync, and a
// counter that climbed forever would make every position unique and defeat
// threefold repetition. It rides in the run snapshot for free, because
// `codec.ts` is structural.

import { type Color, WHITE } from "./board";

/** The numbers a cleared front is worth reporting. */
export interface StageTally {
  /** Moves the tallied side played. */
  moves: number;
  /** Enemy pieces it captured. */
  taken: number;
  /**
   * What those pieces were worth, by `SURRENDER_VALUE`.
   *
   * `taken` is a headcount and this is the damage — a queen and a pawn are the
   * same capture and nine times the loss. The run's score is this added across
   * every front (`runTally + tally`), which is what the counter in the corner
   * reads.
   */
  score: number;
  /** Units its factory pulled out of the ground. */
  mined: number;
  /** Of `taken`, how many were rung out into the void (§31 K4). Absent is nought. */
  ringOuts?: number;
  /** Trophies kept for score rather than exchanged (§31 A2). Absent is nought. */
  trophies?: number;
}

export const EMPTY_TALLY: StageTally = {
  moves: 0,
  taken: 0,
  score: 0,
  mined: 0,
  ringOuts: 0,
  trophies: 0
};

/**
 * Whose play is being counted.
 *
 * The run's side where there is one, and White otherwise — a scenario has no
 * `run` and its player is still the first army, and a two-player game keeps a
 * tally nothing ever reads. One definition rather than three, because a card
 * reporting one side's moves against another side's captures is worse than no
 * card at all.
 */
export const tallySide = (run: { side: Color } | undefined): Color =>
  run?.side ?? WHITE;

/**
 * A tally with `delta` added, as a new object.
 *
 * Never mutated in place: `cloneState` shares this with the state the command
 * was applied *to*, exactly as it shares `run`, so adding to it would edit the
 * previous position's scorecard and a replay would count every move twice.
 *
 * **The base is read defensively, and a run save is why.** A run is stored as a
 * snapshot through a structural codec, so a tally written before a field
 * existed comes back without it — and `undefined + 9` is `NaN`, which then
 * poisons the count for the rest of the run and reads as the counter being
 * broken rather than as an old save. A missing field is nought.
 */
export function addTally(
  tally: StageTally | undefined,
  delta: Partial<StageTally>
): StageTally {
  const base = tally ?? EMPTY_TALLY;
  return {
    moves: (base.moves ?? 0) + (delta.moves ?? 0),
    taken: (base.taken ?? 0) + (delta.taken ?? 0),
    score: (base.score ?? 0) + (delta.score ?? 0),
    mined: (base.mined ?? 0) + (delta.mined ?? 0),
    ringOuts: (base.ringOuts ?? 0) + (delta.ringOuts ?? 0),
    trophies: (base.trophies ?? 0) + (delta.trophies ?? 0)
  };
}

/**
 * The run's score: every closed front plus the one being played.
 *
 * The same `runTally + tally` sum the run-over card makes, named once because
 * a counter on screen and a plate at the end disagreeing about the score is
 * worse than either being wrong on its own.
 */
export const totalScore = (state: {
  tally?: StageTally;
  runTally?: StageTally;
}): number => (state.runTally?.score ?? 0) + (state.tally?.score ?? 0);

/** True when nothing was counted — a card with nothing to report. */
export const tallyIsEmpty = (tally: StageTally | undefined): boolean =>
  !tally || (tally.moves === 0 && tally.taken === 0 && tally.mined === 0);
